import type { UserType } from "@/features/auth/pages/select-user-type";

/**
 * Landing path after a successful login when the user already has a complete
 * profile. Homeowners land on their Home dashboard; every other role lands on
 * the role-branching /dashboard (which renders their own dashboard).
 */
export function getPostAuthLanding(
	userType: UserType | null | undefined,
): string {
	return userType === "homeowner" ? "/home" : "/dashboard";
}

/**
 * Paths belonging to the sign-in and onboarding flow itself.
 *
 * A guard records wherever it found the visitor, and it can find them inside
 * this flow: an un-onboarded visitor who opens `/onboarding/homeowner`
 * directly is bounced to `/onboarding` from `/onboarding/homeowner`. Keeping
 * that as the destination would walk them through the whole form and then
 * return them to the same blank form.
 */
const AUTH_FLOW_PREFIXES = [
	"/login",
	"/verify-otp",
	"/collect-phone",
	"/onboarding",
];

/**
 * The destination a route guard preserved in navigation state, if any.
 *
 * `ProtectedRoute` puts the page a visitor was trying to reach here before
 * diverting them to sign in, collect a phone number, or onboard. Every hop of
 * those flows forwards it, and whichever hop finishes last sends the visitor
 * there instead of to `getPostAuthLanding`.
 *
 * A destination inside the flow is refused rather than forwarded, so the flow
 * cannot become its own destination.
 */
export function readRedirectTo(state: unknown): string | undefined {
	const value = (state as { redirectTo?: unknown } | null)?.redirectTo;
	if (typeof value !== "string" || value === "") return undefined;
	const path = value.split(/[?#]/)[0].replace(/\/+$/, "") || "/";
	const insideFlow = AUTH_FLOW_PREFIXES.some(
		(prefix) => path === prefix || path.startsWith(`${prefix}/`),
	);
	return insideFlow ? undefined : value;
}
